import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchJobs, fetchLibraryCards } from '@app/lib/api';
import { clearSpotlightRecentSearches } from '@app/lib/knowledgeStore';
import { pushToast } from '@app/lib/toast';
import {
  executeSpotlightAction,
  getSlashCommands,
  getSpotlightSuggestions,
  rememberSpotlightSearch,
  searchSpotlight,
  type SpotlightResult,
} from '@app/lib/spotlight';

function groupResults(results: SpotlightResult[]): Array<{ type: SpotlightResult['type']; label: string; results: SpotlightResult[] }> {
  const labels: Record<SpotlightResult['type'], string> = {
    job: 'Jobs',
    card: 'Library Cards',
    sop: 'SOPs',
    glossary: 'Glossary',
    setting: 'Settings',
    incident: 'Incidents',
  };

  const groups = new Map<SpotlightResult['type'], SpotlightResult[]>();
  for (const result of results) {
    const current = groups.get(result.type) || [];
    current.push(result);
    groups.set(result.type, current);
  }

  return Array.from(groups.entries()).map(([type, entries]) => ({
    type,
    label: labels[type],
    results: entries,
  }));
}

export function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs', 'spotlight'],
    queryFn: () => fetchJobs(),
    enabled: open,
  });
  const { data: cards = [] } = useQuery({
    queryKey: ['library', 'spotlight'],
    queryFn: () => fetchLibraryCards(),
    enabled: open,
  });

  const results = useMemo(() => searchSpotlight(jobs, cards, query, 5), [cards, jobs, query]);
  const grouped = useMemo(() => groupResults(results), [results]);
  const flatResults = useMemo(() => grouped.flatMap((group) => group.results), [grouped]);
  const suggestions = useMemo(
    () => getSpotlightSuggestions(jobs, cards, query, [window.location.pathname === '/jobs' ? 'work order' : 'library']),
    [cards, jobs, query],
  );
  const commands = useMemo(() => getSlashCommands(query), [query]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    setActiveIndex(0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (query.startsWith('/')) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => Math.min(current + 1, Math.max(flatResults.length - 1, 0)));
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => Math.max(current - 1, 0));
      }
      if (event.key === 'Enter') {
        const active = flatResults[activeIndex];
        if (!active) return;
        event.preventDefault();
        rememberSpotlightSearch(query);
        navigate(active.target);
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, flatResults, navigate, onClose, open, query]);

  const executeAction = (actionId: string, result: SpotlightResult) => {
    const outcome = executeSpotlightAction(actionId, result, jobs, navigate);
    pushToast(outcome.message, outcome.ok ? 'ok' : 'warn');
    if (outcome.ok && (actionId === 'open' || actionId === 'view' || actionId === 'start-checklist')) {
      rememberSpotlightSearch(query);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="palette-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <button type="button" className="palette-scrim" aria-label="Close search" onClick={onClose} />
          <motion.section
            className="palette"
            role="dialog"
            aria-modal="true"
            aria-label="Search everywhere"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="palette-head">
              <div>
                <p className="eyebrow">Search Everywhere</p>
                <h2 className="panel-title-lg">Jump anywhere</h2>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                Close
              </button>
            </div>

            <div className="palette-search">
              <input
                ref={inputRef}
                className="input palette-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search jobs, cards, SOPs, glossary, settings"
                aria-label="Search everywhere"
              />
              <div className="palette-hints">
                <span>Try: `wo:` `psid:` `mtr:` `tag:`</span>
                <span>Commands: `/new` `/paste` `/library`</span>
              </div>
            </div>

            {query.startsWith('/') ? (
              <div className="stack-sm">
                {commands.map((command) => (
                  <button
                    key={command.id}
                    type="button"
                    className="palette-row"
                    onClick={() => {
                      navigate(command.target);
                      onClose();
                    }}
                  >
                    <div className="list-row-main">
                      <div className="job-customer">/{command.id}</div>
                      <div className="list-row-meta">{command.label}</div>
                    </div>
                  </button>
                ))}
                {commands.length === 0 ? <div className="empty compact">No matching commands.</div> : null}
              </div>
            ) : query.trim().length === 0 ? (
              <div className="stack-md">
                <div className="palette-section">
                  <div className="palette-section-head">
                    <span className="metric-label">Recent & suggested</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        clearSpotlightRecentSearches();
                        pushToast('Recent searches cleared.', 'accent');
                      }}
                    >
                      Clear recent
                    </button>
                  </div>
                  <div className="chip-row">
                    {suggestions.map((suggestion) => (
                      <button key={`${suggestion.source}-${suggestion.value}`} type="button" className="chip" onClick={() => setQuery(suggestion.value)}>
                        {suggestion.value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="palette-results">
                {grouped.map((group) => (
                  <div key={group.type} className="palette-section">
                    <div className="palette-section-head">
                      <span className="metric-label">{group.label}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          const target =
                            group.type === 'job' ? '/jobs'
                              : group.type === 'card' ? '/library?section=cards'
                                : group.type === 'sop' ? '/library?section=sops'
                                  : group.type === 'glossary' ? '/library?section=glossary'
                                    : group.type === 'setting' ? '/settings'
                                      : '/incidents';
                          navigate(target);
                          onClose();
                        }}
                      >
                        See all
                      </button>
                    </div>
                    <div className="stack-sm">
                      {group.results.map((result) => {
                        const rowIndex = flatResults.findIndex((entry) => entry.type === result.type && entry.id === result.id);
                        return (
                          <div
                            key={`${result.type}-${result.id}`}
                            className={rowIndex === activeIndex ? 'palette-row palette-row--active' : 'palette-row'}
                          >
                            <button
                              type="button"
                              className="palette-row-main-button"
                              onClick={() => {
                                rememberSpotlightSearch(query);
                                navigate(result.target);
                                onClose();
                              }}
                            >
                              <div className="list-row-main">
                                <div className="job-customer">{result.title}</div>
                                <div className="list-row-meta">{result.subtitle}</div>
                                <div className="palette-context" dangerouslySetInnerHTML={{ __html: result.matchContext }} />
                                <div className="palette-why">{result.whyMatched}</div>
                              </div>
                            </button>
                            <div className="palette-actions">
                              {result.actions.map((action) => (
                                <button
                                  key={action.id}
                                  type="button"
                                  className="palette-action-btn"
                                  aria-label={action.label}
                                  title={action.label}
                                  onClick={() => executeAction(action.id, result)}
                                >
                                  <span aria-hidden="true">{action.icon}</span>
                                  <span>{action.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {grouped.length === 0 ? <div className="empty compact">No matches yet. Broaden the search or try a prefix.</div> : null}
              </div>
            )}
          </motion.section>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
